import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, CardBody } from "@odingard/design-system";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

type State = "loading" | "ready" | "invalid" | "accepting" | "accepted";

export function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const { user, refresh, setActiveOrg } = useAuth();
  const [state, setState] = useState<State>("loading");
  const [invite, setInvite] = useState<{ email: string; role: string; orgName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!token) {
      setState("invalid");
      setError("This invitation link is missing its token.");
      return;
    }
    api
      .lookupInvitation(token)
      .then((res) => {
        if (!active) return;
        setInvite(res);
        setState("ready");
      })
      .catch((err: unknown) => {
        if (!active) return;
        setState("invalid");
        setError(err instanceof Error ? err.message : "This invitation is invalid or expired.");
      });
    return () => {
      active = false;
    };
  }, [token]);

  async function accept() {
    if (!token) return;
    setState("accepting");
    setError(null);
    try {
      const res = await api.acceptInvitation(token);
      await refresh();
      setActiveOrg(res.organizationId);
      setState("accepted");
      setTimeout(() => navigate("/studio"), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not accept the invitation.");
      setState("ready");
    }
  }

  const next = `/accept-invite?token=${encodeURIComponent(token ?? "")}`;

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <Link to="/" className="brand" style={{ justifyContent: "center", marginBottom: "var(--ods-space-5)" }}>
          <span className="brand__mark">O</span> Odingard Studio
        </Link>
        <Card>
          <CardBody>
            <h1 style={{ fontSize: "var(--ods-text-xl)", marginTop: 0 }}>Team invitation</h1>
            {state === "loading" && <p>Loading your invitation…</p>}
            {state === "invalid" && (
              <div className="banner banner--warning" role="alert">
                {error}
              </div>
            )}
            {(state === "ready" || state === "accepting") && invite && (
              <>
                <p>
                  You&apos;ve been invited to join <strong>{invite.orgName}</strong> as{" "}
                  <strong>{invite.role}</strong>.
                </p>
                {error && (
                  <div className="banner banner--warning" role="alert">
                    {error}
                  </div>
                )}
                {!user ? (
                  <>
                    <div className="banner banner--info" role="status">
                      Sign in as <strong>{invite.email}</strong> to accept this invitation.
                    </div>
                    <Link to={`/login?next=${encodeURIComponent(next)}`}>
                      <Button block>Sign in to accept</Button>
                    </Link>
                    <p style={{ textAlign: "center", fontSize: "var(--ods-text-sm)", color: "var(--ods-color-muted)" }}>
                      New here? <Link to={`/start`}>Create an account</Link> with {invite.email}, then reopen this link.
                    </p>
                  </>
                ) : user.email.toLowerCase() !== invite.email.toLowerCase() ? (
                  <div className="banner banner--warning" role="alert">
                    This invitation is for {invite.email}, but you&apos;re signed in as {user.email}. Sign in with the
                    invited email to accept.
                  </div>
                ) : (
                  <Button block disabled={state === "accepting"} onClick={() => void accept()}>
                    {state === "accepting" ? "Joining…" : `Join ${invite.orgName}`}
                  </Button>
                )}
              </>
            )}
            {state === "accepted" && invite && (
              <div className="banner banner--success" role="status">
                You&apos;ve joined <strong>{invite.orgName}</strong>. Redirecting to Studio…
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
